/* The rows of a choice control: RadioGroup and Checkbox. `variant="card"` is
 * a bordered, full-width row at least 44px tall (the WCAG 2.2 target floor)
 * with the 8px `rounded` corner from lib/shape.ts. The checked row reads the
 * one SELECTED paint that Card `selected` uses (lib/surfaces.ts), so no second
 * copy of the teal edge and glow exists; an unchecked, enabled row takes the
 * generated hover-tint. The option fields (description, icon, trailing, a
 * disabled reason) share the classes below in both variants. Engine tier, not
 * in the barrel. Literal class strings so Tailwind sees them. */
import { SHAPE_CLASS } from "./shape.js";
import { SELECTED } from "./surfaces.js";
import { cn } from "./utils.js";

/** Visual style of a choice control: today's inline row, or a card row. */
export type ChoiceVariant = "default" | "card";

/* The checked paint animates both ways and stops under reduced motion. */
const CARD_ROW =
  "flex w-full min-h-11 items-center border bg-pb-card transition-[border-color,box-shadow] duration-motion-state ease-motion-standard motion-reduce:transition-none";

/** The card row: box, corner, and the checked or hover paint. */
export function choiceCardRow(checked: boolean, disabled: boolean): string {
  return cn(
    CARD_ROW,
    SHAPE_CLASS.rounded,
    checked ? SELECTED : "border-pb-border",
    !checked && !disabled && "hover-tint",
  );
}

/** The label of a card row. It fills the row, so the whole row picks the
 *  option; only the trailing slot sits outside it. */
export const CHOICE_CARD_LABEL =
  "flex min-w-0 flex-1 items-center gap-3 self-stretch px-3 py-2 select-none";

/** The trailing slot of a card row, at its end edge. */
export const CHOICE_CARD_TRAILING = "shrink-0 pe-3";

/** A default row that has a trailing slot: the label, then the slot at the
 *  end edge. Without a trailing slot the label is the row, as today. */
export const CHOICE_ROW = "flex items-center justify-between gap-3";

/** The trailing slot of a default row. */
export const CHOICE_TRAILING = "shrink-0";

/** The label, description and reason stacked in one column. */
export const CHOICE_TEXT = "flex min-w-0 flex-col";

/** A description or a disabled reason under the label. */
export const CHOICE_DETAIL = "text-xs text-pb-text-muted";

/** A decorative icon before the label. */
export const CHOICE_ICON = "inline-flex shrink-0 text-pb-text-muted";

/** The dimmed parts of an option that cannot be chosen. */
export const CHOICE_DIMMED = "opacity-50";
